<template>
  <PlainCard>
    <FormBuilder
      v-bind="formProps"
      :inputProperties="resultInputProperties"
      @submit="onSubmit"
    >
      <template #belowForm>
        <v-alert
          v-if="registeredUserInfo"
          type="success"
          :value="true"
          closable
          @update:model-value="registeredUserInfo = null"
        >
          {{ lt('account_created') }}<br>
          {{ lt('id_of_created_user') }}: {{ registeredUserInfo.accountId }}<br>
        </v-alert>
      </template>
    </FormBuilder>

    <v-dialog v-model="otpDialogOpen" persistent max-width="480">
      <v-card class="pa-3">
        <FieldConfirmation class="pa-2 border-sm rounded-lg"
          v-if="currentOtpAlias"
          :key="currentOtpAlias"
          :alias="currentOtpAlias"
          :value="currentOtpValue"
          :optionalConfirm="isCurrentOtpOptional"
          :updateProfile="false"
          :textRetryIn="props.textRetryIn"
          :textRetryNow="props.textRetryNow"
          @confirmed="onOtpConfirmed"
        />
        <v-card-actions>
          <v-btn
            v-if="isCurrentOtpOptional"
            variant="text"
            @click="onOtpSkip"
          >
            {{ lt('skip') }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="text"
            @click="onOtpCancel"
          >
            {{ lt('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </PlainCard>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { RegisterMemberProps, ConfirmationMode, defaults } from './RegisterMember.ts';

import { FormResult, genFormFieldFromAlias } from '@/models/form-builder-interface';
import { CONFIRMABLE_FIELDS_ALIASES, ConfirmableFieldAlias, ConfirmationResult } from '@/models/field-confirmation-interface';

import { useAccountApi, useAccountCreationApi } from '@ui-api3-sdk/api/api3';
import { useAwaitAccountId, useEnrollSettings, useFormBuilder, useLocalization, useToast } from '@/composables';
import { usePropertiesStore } from '@/pinia/usePropertiesStore';
import { resolveListStatic } from '@/utils/properties-mapping-service';
import { generatePassword } from '@/utils/utils';

import FormBuilder from './base/FormBuilder.vue';
import FieldConfirmation from './base/FieldConfirmation.vue';
import PlainCard from './base/PlainCard.vue';

const { lt } = useLocalization('uiRegisterMember', [
  'email_not_unique',
  'phone_not_unique',
  'account_created',
  'unknown_uniqueness_error',
  'unknown_create_error',
  'id_of_created_user',
  'skip',
  'cancel',
]);

const props = withDefaults(defineProps<RegisterMemberProps>(), {
  id: undefined,
  fieldTitles: undefined,
  optionalFields: undefined,
  forceFields: undefined,
  otpConfirm: undefined,
  profileFields: () => defaults.profileFields,
  inputProperties: () => defaults.inputProperties,
  languageMode: () => defaults.languageMode,
});

const { inputProperties: resultInputProperties } = useEnrollSettings(props.inputProperties);
const { props: formProps, methods: form } = useFormBuilder([]);

const languagesFromApi = ref<Array<{ key: string; value: string }>>([]);

type RegisteredUserInfo = {
  name: string,
  accountId: string;
}

const CUSTOM_FIELDS = resolveListStatic('custom').map(p => p.replace('custom.', ''));

const registeredUserInfo = ref<RegisteredUserInfo | null>(null);

// OTP state
const otpDialogOpen = ref(false);
const otpQueue = ref<ConfirmableFieldAlias[]>([]);
const currentOtpAlias = ref<ConfirmableFieldAlias | null>(null);
const otpHeaders = new Map<ConfirmableFieldAlias, ConfirmationResult>();
let pendingProfile: Record<string, any> | null = null;

const currentOtpValue = ref<string | undefined>(undefined);
const isCurrentOtpOptional = ref(false);

function isCustomProperty(alias: string): boolean {
  return CUSTOM_FIELDS.includes(alias);
}

onMounted(async () => {
  await usePropertiesStore().getPfAsync();
  await loadLanguages();

  const fields = props.profileFields.map(alias => {
    const field = {
      ... genFormFieldFromAlias(`${ isCustomProperty(alias) ? 'custom' : 'p' }.${alias}`),
      isMandatory: ! isFieldOptional(alias),
      alias,
    };
    if (props.fieldTitles?.[alias]) {
      field.title = props.fieldTitles[alias];
    }
    if (alias === 'language_id') {
      if (props.languageMode === 'hidden_no_api' || props.languageMode === 'hidden_auto') {
        field.hidden = true;
        field.disabled = true;
      } else if (props.languageMode === 'editable_auto') {
        field.hidden = false;
        field.disabled = false;
      }
    }
    return field;
  });

  form.setFields(fields);
  setupLanguageField();
});

function getFormValues(res: FormResult): Record<string, any> {
  const profile = { ...res };
  Object.keys(profile).forEach(k => { if (!profile[k]) delete profile[k]; });
  CUSTOM_FIELDS.forEach(cf => delete profile[cf]);
  Object.entries(props.forceFields || {}).forEach(([k, v]) => { profile[k] = v; });
  return withLanguageInProfile(profile);
}

const onSubmit = async (res: FormResult) => {
  const { phone: rawPhone, email } = res;
  const phone = rawPhone ? `+${rawPhone.replace(/[^\d]/g, '')}` : rawPhone;

  form.setLoading(true);

  const uniqueCheckResult = await useAccountApi()
    .otpUniquenessCheck({
      uniquenessCheckReq: {
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
      },
    })
    .catch(err => {
      if (form.parseApiValidation(err)) return;
      form.addError(lt('unknown_uniqueness_error'));
    })
    .finally(() => {
      form.setLoading(false);
    });

  if (!uniqueCheckResult?.data?.payload) return;
  const { emailUnique, phoneUnique } = uniqueCheckResult?.data?.payload || {};

  if ( email && emailUnique === false ) form.addFieldError('email', lt('email_not_unique'));
  if ( phone && phoneUnique === false ) form.addFieldError('phone', lt('phone_not_unique'));

  if ( form.hasErrors() ) return;

  const profile = getFormValues(res);

  const confirmConfig = props.otpConfirm;
  if (confirmConfig) {
    const queue = CONFIRMABLE_FIELDS_ALIASES.filter(alias => {
      const mode = confirmConfig[alias];
      if (!mode) return false;
      if (mode === 'if-filled') return true;
      return !!res[alias];
    });

    if (queue.length) {
      otpHeaders.clear();
      pendingProfile = profile;
      otpQueue.value = queue;
      openNextOtpDialog();
      return;
    }
  }

  await createAccount(profile);
};

function openNextOtpDialog() {
  const alias = otpQueue.value[0];
  if (!alias) {
    otpDialogOpen.value = false;
    if (pendingProfile) createAccount(pendingProfile);
    return;
  }

  const mode = props.otpConfirm?.[alias] as ConfirmationMode;
  currentOtpAlias.value = alias;
  currentOtpValue.value = form.hasField(alias) ? form.getField(alias).value : undefined;

  if (mode === 'if-filled' && !currentOtpValue.value) {
    otpQueue.value = otpQueue.value.slice(1);
    openNextOtpDialog();
    return;
  }

  isCurrentOtpOptional.value = mode === 'optional';
  otpDialogOpen.value = true;
}

function onOtpConfirmed(result: ConfirmationResult) {
  otpHeaders.set(result.field, result);
  otpQueue.value = otpQueue.value.slice(1);
  openNextOtpDialog();
}

function onOtpSkip() {
  otpQueue.value = otpQueue.value.slice(1);
  openNextOtpDialog();
}

function onOtpCancel() {
  otpDialogOpen.value = false;
  currentOtpAlias.value = null;
  otpQueue.value = [];
  pendingProfile = null;
  otpHeaders.clear();
  form.setLoading(false);
}

async function createAccount(profile: Record<string, any>) {
  form.setLoading(true);

  const aId = await useAwaitAccountId();

  const gluedOtpHeaders = Array.from(otpHeaders.values())
    .map(h => h.otpHeader['x-otp-check'])
    .join('|');

  const requestOptions = gluedOtpHeaders
    ? { headers: { 'x-otp-check': gluedOtpHeaders } }
    : undefined;

  useAccountCreationApi()
    .accountCreateControllerCreateAccount({
      accountCreateReq: {
        login: profile.email,
        password: getPasswordValue(),
        sponsorId: aId,
        profile,
      },
    }, requestOptions)
    .then((res) => {
      const info = res.data.payload.info as any;

      registeredUserInfo.value = {
        name:      info.title as string,
        accountId: info.account.id as string,
      };

      useToast().ok(lt('account_created'));
      form.reset();
      form.clearErrors();
      otpHeaders.clear();
      pendingProfile = null;
    })
    .catch(err => {
      if (form.parseApiValidation(err)) return;
      form.addError(lt('unknown_create_error'));
    })
    .finally(() => {
      form.setLoading(false);
    });
}

function getPasswordValue() {
  if (form.hasField('password')) return form.getField('password').value;

  return generatePassword({
    size: props.inputProperties?.passwordMinLength || 6,
    hasUpper: props.inputProperties.passwordHasCamelcase || false,
    hasSpecial: props.inputProperties.passwordHasSpecSymbols || false,
  });
}

function isFieldOptional(alias: string) {
  if (props.otpConfirm?.[alias] === true) return false;
  return props.optionalFields?.includes(alias);
}

async function loadLanguages() {
  try {
    await usePropertiesStore().fetchLanguagesList();
    languagesFromApi.value = usePropertiesStore().getLanguagesList();
  } catch (err) {
    console.warn('Languages list cant be fetched', err);
  }
}

function detectBrowserLanguage(): string | undefined {
  const nav = window.navigator as any;
  const raw: string | undefined = nav?.language || (Array.isArray(nav?.languages) ? nav.languages[0] : undefined);
  if (!raw) return undefined;
  return String(raw).toLowerCase().split('-')[0];
}

function matchLanguageKey(preferred: string | undefined, allowedKeys: string[]): string | undefined {
  if (!preferred) return undefined;
  const short = String(preferred).toLowerCase();
  if (allowedKeys.includes(short)) return short;
  const exact = allowedKeys.find(k => String(k).toLowerCase() === short);
  if (exact) return exact;
  const starts = allowedKeys.find(k => String(k).toLowerCase().startsWith(short + '-'));
  return starts;
}

function getAllowedLanguageKeys(): string[] {
  const langProp = usePropertiesStore().findPropertyDefinition('p', 'language_id');
  const dictKeys = langProp?.dictionary?.dictionaryItems?.map((i: any) => i.key) || [];
  if (dictKeys.length) return dictKeys as string[];
  return languagesFromApi.value.map(i => i.key);
}

function setupLanguageField() {
  const mode = props.languageMode;
  if (!mode) return;

  const lang = detectBrowserLanguage();
  const allowedKeys = getAllowedLanguageKeys();

  const field = form.hasField('language_id');
  if (field) {
    if (mode === 'hidden_no_api') {
      field.hidden = true;
      field.disabled = true;
    } else if (mode === 'hidden_auto') {
      field.hidden = true;
      field.disabled = true;
      const matched = matchLanguageKey(lang, allowedKeys);
      if (matched) field.value = matched;
    } else if (mode === 'editable_auto') {
      field.hidden = false;
      field.disabled = false;
      const matched = matchLanguageKey(lang, allowedKeys);
      if (matched && (field.value === undefined || field.value === null || field.value === ''))
        field.value = matched;
    }
  } else if (mode === 'editable_auto') {
    const matched = matchLanguageKey(lang, allowedKeys);
    const newField = {
      ...genFormFieldFromAlias('p.language_id'),
      alias: 'language_id',
      isMandatory: !isFieldOptional('language_id'),
      hidden: false,
      disabled: false,
      value: matched,
    };
    formProps.modelValue = [...formProps.modelValue, newField];
  }
}

function withLanguageInProfile(profile: Record<string, any>): Record<string, any> {
  const mode = props.languageMode;
  if (mode === 'hidden_no_api') return profile;

  const field = form.hasField('language_id');
  const current = field?.value;
  const detected = detectBrowserLanguage();
  const value = current || detected;

  const allowedKeys = getAllowedLanguageKeys();
  const matched = matchLanguageKey(value, allowedKeys);
  if (matched) profile['language_id'] = matched;

  return profile;
}

</script>
